import React, { useState, useEffect } from 'react';
import { ChevronRight, Minus, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardService from "../dashboard.service";
import {
    useMediaQuery,
    Box,
    Typography,
    IconButton,
    Button,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from '@mui/material';

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '40px',
        marginBottom: '32px',
        fontSize: '14px',
        color: '#666'
    },
    productGridSmall: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        marginBottom: '48px'
    },
    productGridLarge: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '32px',
        marginBottom: '48px'
    },
    imageGallerySmall: {
        display: 'flex',
        gap: '16px',
        flexDirection: 'column-reverse'
    },
    imageGalleryLarge: {
        display: 'flex',
        gap: '16px'
    },
    thumbnailContainerLarge: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    thumbnailContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px'
    },

    thumbnail: {
        width: '64px',
        height: '64px',
        border: '1px solid #3488A4',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer'
    },
    mainImage: {
        flex: 1,
        border: '1px solid #3488A4',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    productTitle: {
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '8px',
        color: '#1a202c'
    },
    refNo: {
        color: '#666',
        fontSize: '14px'
    },
    price: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#3488A4',
        margin: '24px 0'
    },
    checkItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#48bb78'
    },
    quantityLabel: {
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: '8px'
    },
    quantityContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    quantityControl: {
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #3488A4',
        borderRadius: '6px'
    },
    quantityButton: {
        padding: '8px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#4a5568',
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
            color: '#ccc'
        }
    },
    quantityDisplay: {
        padding: '8px 16px',
        borderLeft: '1px solid #3488A4',
        borderRight: '1px solid #3488A4'
    },
    availability: {
        marginTop: '16px',
        fontSize: '14px'
    },
    inStock: {
        color: '#3488A4',
        fontWeight: '500'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '24px'
    },
    addToCartButton: {
        padding: '12px',
        backgroundColor: '#ebf8ff',
        color: '#3488A4',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        transition: 'all 0.3s ease',
        '&:disabled': {
            backgroundColor: '#f5f5f5',
            color: '#999',
            cursor: 'not-allowed'
        }
    },
    RemoveFromCartButton: {
        padding: '12px',
        backgroundColor: 'red',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500'
    },

    buyNowButton: {
        padding: '12px',
        backgroundColor: '#3488A4',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        transition: 'all 0.3s ease',
        '&:disabled': {
            backgroundColor: '#ccc',
            color: '#999',
            cursor: 'not-allowed'
        }
    },
    productInfo: {
        marginTop: '24px'
    },
    brand: {
        marginBottom: '8px'
    },
    productDetailsSection: {
        marginTop: '48px'
    },
    productDetailsTitle: {
        fontSize: '32px',
        color: '#3488A4',
        marginBottom: '24px'
    },
    accordion: {
        marginBottom: '16px'
    },
    accordionButton: {
        width: '100%',
        padding: '16px',
        backgroundColor: '#3488A4',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer'
    },
    accordionContent: {
        padding: '16px',
        backgroundColor: 'white',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        borderLeft: '1px solid #3488A4',
        borderRight: '1px solid #3488A4',
        borderBottom: '1px solid #3488A4'
    },
    relatedSection: {
        marginTop: '64px'
    },
    relatedTitle: {
        fontSize: '32px',
        color: '#3488A4',
        marginBottom: '32px'
    },
    relatedGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px'
    }
};

const ProductDetail = ({ product, setShowLoginPopup, ProductGrid, products, handleIncrement,
    handleDecrement, getProductQuantity, handleVariationChange,
    tempCart, setProduct_Details, handleAddToCart, productStock }) => {
    const [selectedImage, setSelectedImage] = useState(0);
    const [openSection, setOpenSection] = useState('description');
    const user = JSON.parse(localStorage.getItem("user"));
    const [quantity, setQuantity] = useState(getProductQuantity(product._id));
    const [productInCart, setProductInCart] = useState(false);
    const [localProductStock, setLocalProductStock] = useState(productStock || {});
    
    // Use localProductStock if productStock prop is not available
    const effectiveProductStock = productStock || localProductStock;
    
    // Check if product is available in stock
    const isProductInStock = effectiveProductStock && effectiveProductStock[product._id] > 0;
    const availableStock = effectiveProductStock && effectiveProductStock[product._id] ? effectiveProductStock[product._id] : 0;
    
    // Function to fetch stock information when component is accessed directly
    const fetchProductStock = async () => {
        if (!productStock && product?._id) {
            try {
                const updateData = {
                    "functionName": "GetProductStock",
                    "postData": {
                        "item_id": product._id,
                        "business_id": ""
                    }
                };
                const response = await DashboardService.GetProducctDetails(updateData);
                if (response.data.status === true) {
                    // Extract stock information from response
                    const stockInfo = response.data.data[0]?.stock || 0;
                    setLocalProductStock(prev => ({
                        ...prev,
                        [product._id]: stockInfo
                    }));
                }
            } catch (error) {
                console.error('Error fetching product stock:', error);
                // Set default stock as 0 if fetch fails
                setLocalProductStock(prev => ({
                    ...prev,
                    [product._id]: 0
                }));
            }
        }
    };

    const handleQuantityIncrement = () => {
        // Only allow increment if product is in stock and adding one more wouldn't exceed available stock
        if (isProductInStock && quantity < availableStock) {
            setQuantity(prev => prev + 1);
        }
    };

    const handleQuantityDecrement = () => {
        setQuantity(prev => (prev > 0 ? prev - 1 : 0));
    };
    
    const isSmallScreen = useMediaQuery('(max-width: 768px)');
    
    useEffect(() => {
        // Check if the product is in the cart
        const inCart = tempCart.some(item => item.product_id === product._id);
        setProductInCart(inCart);
        setQuantity(getProductQuantity(product._id));
    }, [tempCart, product._id]);

    // Effect to handle stock changes
    useEffect(() => {
        if (effectiveProductStock && effectiveProductStock[product._id] !== undefined) {
            // If product goes out of stock, reset quantity to 0
            if (effectiveProductStock[product._id] <= 0) {
                setQuantity(0);
            } else {
                // If product comes back in stock, ensure quantity doesn't exceed available stock
                const currentQuantity = getProductQuantity(product._id);
                if (currentQuantity > effectiveProductStock[product._id]) {
                    setQuantity(effectiveProductStock[product._id]);
                }
            }
        }
    }, [effectiveProductStock, product._id, getProductQuantity]);

    const HandleLogin = () => {
        if (!user) {
            setShowLoginPopup(true);
        }
    }

    const addToCart = () => {
        // Check if product is in stock before adding to cart
        if (!isProductInStock) {
            return; // Don't add to cart if out of stock
        }
        
        if (quantity > 0) {
            handleAddToCart(product, quantity, true);
        } else {
            handleAddToCart(product, 1, true);
        }
    };
    
    const RemoveFromCart = () => {
        if (productInCart) {
            handleAddToCart(product, 0, true);
        } else {
            setQuantity(0);
        }
    };

    // const handleDecrement = (product) => {
    //     let newQuantity = quantity > 0 ? quantity - 1 : 0;
    //     console.log("newQuantity", newQuantity)
    //     const productInCart = tempCart.some(item => item.product_id === product._id);
    //     if (newQuantity === 0 && productInCart) {
    //       handleAddToCart(product, 0)
    //     }
    //     setQuantity(newQuantity);
    //   };




    const toggleSection = (section) => {
        setOpenSection(openSection === section ? '' : section);
    };

    const id = product?._id
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showReviews, setShowReviews] = useState(false);
    const [productData, setProductData] = useState(null);
    const [reviewsData, setReviewsData] = useState([]);
    const [imageIndex, setImageIndex] = useState([]);
    const [thumbnails, setThumbnails] = useState([]);

    useEffect(() => {
        fetchProductDetails();
    }, []);

    // Fetch stock information if not provided via props
    useEffect(() => {
        if (!productStock && product?._id) {
            fetchProductStock();
        }
    }, [product?._id, productStock]);

    const fetchProductDetails = async () => {
        setLoading(true);
        try {
            const updateData = {
                "functionName": "SearchItem",
                "postData": {
                    "item_id": id,
                    "business_id": ""
                }
            };
            const response = await DashboardService.GetProducctDetails(updateData);
            if (response.data.status === true) {
                const product = response.data.data[0];
                const imageIndex = product.index || 0;
                setThumbnails(product.Images)
                setImageIndex(imageIndex)
                setProductData(product);
            }
        } catch (error) {
            console.error('Error fetching product details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const updateData = {
                "functionName": "ProductReviews",
                "postData": {
                    "item_id": id
                }
            };
            const response = await DashboardService.GeProductReviews(updateData);
            if (response.data.status === true) {
                setReviewsData(response.data.reviewsData.data);
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    // const quantity = getProductQuantity(product._id);

    return (
        <div style={styles.container}>
            {/* Breadcrumb */}
            <nav style={styles.breadcrumb}>
                <span>Home</span>
                <ChevronRight size={16} />
                <span>{productData?.CategoryDetails[0].Name}</span>
                <ChevronRight size={16} />
                <span 
                  style={{ 
                    color: '#1a202c',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: '150px',
                  }}
                  title={productData?.item_name}
                >
                  {productData?.item_name}
                </span>
            </nav>

            <div style={isSmallScreen ? styles.productGridSmall : styles.productGridLarge}>
                {/* Product Images */}
                <div style={isSmallScreen ? styles.imageGallerySmall : styles.imageGalleryLarge}>
                    {/* Thumbnails */}
                    <div style={isSmallScreen ? styles.thumbnailContainer : styles.thumbnailContainerLarge}>
                        {thumbnails?.map((thumb, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setSelectedImage(index)
                                    setImageIndex(index)
                                }}
                                style={{
                                    ...styles.thumbnail,
                                    borderColor: selectedImage === index ? '#3488A4' : '#3488A4'
                                }}
                            >
                                <img src={thumb} alt={`Thumbnail ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </button>
                        ))}
                    </div>

                    {/* Main Image */}
                    <div style={styles.mainImage}>
                        <img
                            src={thumbnails[imageIndex]}
                            alt="product"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                </div>

                {/* Product Info */}
                <div>
                    <h1
                      style={{
                        ...styles.productTitle,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%'
                      }}
                    >
                      {productData?.item_name} {productData?.BaseUOM}
                    </h1>
                    
                    {/* Stock Status Indicator - only show Out of Stock (0) or Limited stock (<30) */}
                    {effectiveProductStock && effectiveProductStock[product._id] !== undefined && (
                        effectiveProductStock[product._id] === 0 ? (
                            <div style={{
                                display: 'inline-block',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '600',
                                marginBottom: '16px',
                                backgroundColor: '#f8d7da',
                                color: '#dc3545',
                                border: '1px solid #dc3545'
                            }}>
                                ❌ Out of Stock
                            </div>
                        ) : effectiveProductStock[product._id] < 30 ? (
                            <div style={{
                                display: 'inline-block',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '600',
                                marginBottom: '16px',
                                backgroundColor: '#fff3e0',
                                color: '#ff6b35',
                                border: '1px solid #ff6b35'
                            }}>
                                ⚠️ Limited stock
                            </div>
                        ) : null
                    )}
                    
                    {/* <p style={styles.refNo}>Ref No: {productData?.item_code}</p> */}
                    <p style={styles.price}>RM{parseFloat(productData?.price).toFixed(2)}</p>

                    <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: isSmallScreen ? 'wrap' : 'nowrap' }}>
                        {/* <div style={styles.checkItem}>
                            <span>✓</span>
                            <span>Free Delivery</span>
                        </div> */}
                        {/* <div style={styles.checkItem}>
                            <span>✓</span>
                            <span>Securely Packed</span>
                        </div> */}
                    </div>

                    {/* Quantity Selector */}
                    <div>
                        <label style={styles.quantityLabel}>Quantity</label>
                        <div style={styles.quantityContainer}>
                            <div style={styles.quantityControl}>
                                <button 
                                    onClick={handleQuantityDecrement} 
                                    style={styles.quantityButton}
                                    disabled={!isProductInStock}
                                >
                                    <Minus size={16} />
                                </button>
                                <span style={styles.quantityDisplay}>{quantity}</span>
                                <button 
                                    onClick={handleQuantityIncrement} 
                                    style={styles.quantityButton}
                                    disabled={!isProductInStock || quantity >= availableStock}
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            {/* Stock Status Display - only show Out of Stock (0) or Limited stock (<30) */}
                            {effectiveProductStock && effectiveProductStock[product._id] !== undefined && (
                                effectiveProductStock[product._id] === 0 ? (
                                    <span style={{ 
                                        color: '#dc3545', 
                                        fontSize: '12px',
                                        fontWeight: '500'
                                    }}>
                                        Out of Stock
                                    </span>
                                ) : effectiveProductStock[product._id] < 30 ? (
                                    <span style={{ 
                                        color: '#ff6b35', 
                                        fontSize: '12px',
                                        fontWeight: '500'
                                    }}>
                                        Limited stock
                                    </span>
                                ) : null
                            )}
                        </div>
                    </div>

                    {/* <div style={styles.availability}>
                        <span>Availability: </span>
                        <span style={styles.inStock}>IN STOCK</span>
                    </div> */}

                    <div style={styles.buttonContainer}>
                        {/* Show warning if product is in cart but out of stock */}
                        {productInCart && !isProductInStock && (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffeaa7',
                                borderRadius: '6px',
                                color: '#856404',
                                fontSize: '14px',
                                marginBottom: '12px'
                            }}>
                                ⚠️ This item is currently out of stock but remains in your cart. It will be removed when stock becomes available.
                            </div>
                        )}
                        
                        <button 
                            onClick={addToCart} 
                            style={{
                                ...styles.addToCartButton,
                                opacity: isProductInStock ? 1 : 0.5,
                                cursor: isProductInStock ? 'pointer' : 'not-allowed'
                            }}
                            disabled={!isProductInStock}
                        >
                            {isProductInStock ? 'Add To Cart' : 'Out of Stock'}
                        </button>
                        {quantity > 0 &&
                            <button onClick={RemoveFromCart} style={styles.RemoveFromCartButton}>
                                Remove from Cart
                            </button>
                        }
                        {!user &&
                            <button 
                                onClick={() => { HandleLogin() }} 
                                style={{
                                    ...styles.buyNowButton,
                                    opacity: isProductInStock ? 1 : 0.5,
                                    cursor: isProductInStock ? 'pointer' : 'not-allowed'
                                }}
                                disabled={!isProductInStock}
                            >
                                {isProductInStock ? 'Sign in & Buy Now' : 'Out of Stock'}
                            </button>
                        }
                    </div>
                    {productInCart && product.GroupedVariations && product.GroupedVariations.length > 0 && (
                        <Box mt={4}>
                            <Typography variant="h6" gutterBottom>
                                Variations
                            </Typography>
                            {product.GroupedVariations.map((variationGroup) => (
                                <Box key={variationGroup.Variation._id} mb={2}>
                                    <Typography variant="body2">{variationGroup.Variation.name}</Typography>
                                    <FormGroup row>
                                        {variationGroup.VariationTypes.map((type) => (
                                            <FormControlLabel
                                                key={type._id}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        onChange={(e) =>
                                                            handleVariationChange(product, variationGroup, type, e.target.checked)
                                                        }
                                                        checked={tempCart.some(
                                                            (item) =>
                                                                item.product_id === product._id &&
                                                                item.variations.some(
                                                                    (v) =>
                                                                        v.variation_id === variationGroup.Variation._id &&
                                                                        v.variation_option_id === type._id
                                                                )
                                                        )}
                                                    />
                                                }
                                                label={`${type.variation_type_name} (+RM${parseFloat(type.price).toFixed(2)})`}
                                            />
                                        ))}
                                    </FormGroup>
                                </Box>
                            ))}
                        </Box>
                    )}
                    <div style={styles.productInfo}>
                        {/* <div style={styles.brand}>
                            <span style={{ fontWeight: '500' }}>Brand: </span>
                            <span style={{ color: '#3488A4' }}>GSK CONSUMER HEALTH</span>
                        </div> */}
                        <div>
                            <span style={{ fontWeight: '500' }}>Category: </span>
                            <span style={{ color: '#666' }}>
                                {product.CategoryDetails[0].Name}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Details Section */}
            <div style={styles.productDetailsSection}>
                <h2 style={styles.productDetailsTitle}>Product Details</h2>

                {/* Accordions */}
                <div style={styles.accordion}>
                    <button
                        style={styles.accordionButton}
                        onClick={() => toggleSection('description')}
                    >
                        <span>Product Description</span>
                        {openSection === 'description' ? <ChevronUp /> : <ChevronDown />}
                    </button>
                    {openSection === 'description' && (
                        <div style={styles.accordionContent}>
                            {productData?.description}
                            
                            {/* Stock Information */}
                            {effectiveProductStock && effectiveProductStock[product._id] !== undefined && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <strong>Stock Information:</strong>
                                    {effectiveProductStock[product._id] === 0 ? (
                                        <span style={{ color: '#dc3545' }}>
                                            {' '}This product is currently out of stock. Please check back later or contact us for availability updates.
                                        </span>
                                    ) : effectiveProductStock[product._id] < 30 ? (
                                        <span style={{ color: '#ff6b35' }}>
                                            {' '}Limited stock available.
                                        </span>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* <div style={styles.accordion}>
                    <button
                        style={styles.accordionButton}
                        onClick={() => toggleSection('delivery')}
                    >
                        <span>Delivery</span>
                        {openSection === 'delivery' ? <ChevronUp /> : <ChevronDown />}
                    </button>
                    {openSection === 'delivery' && (
                        <div style={styles.accordionContent}>
                            Delivery information goes here...
                        </div>
                    )}
                </div> */}

                {/* <div style={styles.accordion}>
                    <button
                        style={styles.accordionButton}
                        onClick={() => toggleSection('returns')}
                    >
                        <span>Return & Exchange Policy</span>
                        {openSection === 'returns' ? <ChevronUp /> : <ChevronDown />}
                    </button>
                    {openSection === 'returns' && (
                        <div style={styles.accordionContent}>
                            Return and exchange policy information goes here...
                        </div>
                    )}
                </div> */}
            </div>

            {/* Related Products */}
            {/* <div style={styles.relatedSection}>
                <h2 style={styles.relatedTitle}>Related Products</h2>
                <div style={styles.relatedGrid}>
                    <ProductGrid
                        handleAddToCart={handleAddToCart}
                        setProduct_Details={setProduct_Details}
                        products={products}
                        handleIncrement={handleIncrement}
                        handleDecrement={handleDecrement}
                        getProductQuantity={getProductQuantity}
                        tempCart={tempCart}
                        handleVariationChange={handleVariationChange}
                    />
                </div>
            </div> */}
        </div>
    );
};

export default ProductDetail;